export type PrioridadTarea = 'baja' | 'media' | 'alta' | 'urgente';
export type EntidadTipoTarea = 'paciente' | 'consulta' | 'cita' | 'presupuesto';

export interface Tablero {
  id: string;
  nombre: string;
  descripcion: string | null;
  orden: number;
  archivado: boolean;
}

export interface TableroColumna {
  id: string;
  tableroId: string;
  nombre: string;
  color: string | null;
  orden: number;
  esFinal: boolean;
  limiteWip: number | null;
}

export interface TareaAsignado {
  id: string;
  nombre: string;
  fotoUrl: string | null;
}

export interface EtiquetaTarea {
  id: string;
  nombre: string;
  color: string;
}

export interface TareaRelacion {
  id: string;
  tipo: EntidadTipoTarea;
  entidadId: string | null;
  pacienteId: string | null;
  /** Cita asociada (la propia cita o la de la consulta). `/consultas/:id` se abre con el id de la CITA. */
  citaId: string | null;
  label: string;
  fecha: string | null;
  oculto: boolean;
}

export interface Tarea {
  id: string;
  tableroId: string;
  columnaId: string;
  columnaNombre: string | null;
  tableroNombre: string | null;
  parentId: string | null;
  consultorioId: string | null;
  titulo: string;
  tieneDescripcion: boolean;
  prioridad: PrioridadTarea;
  fechaVencimiento: string | null;
  orden: number;
  completadaAt: string | null;
  creadoPorId: string | null;
  createdAt: string;
  updatedAt: string;
  asignados: TareaAsignado[];
  etiquetas: EtiquetaTarea[];
  relaciones: TareaRelacion[];
  subtareas: { total: number; completadas: number };
  checklist: { total: number; completados: number };
  comentarios: number;
  adjuntos: number;
}

export interface TareaDetalle extends Tarea {
  descripcion: string | null;
  subtareasLista: Tarea[];
  columna: { id: string; nombre: string; esFinal: boolean } | null;
  tablero: { id: string; nombre: string } | null;
}

export interface TableroCompleto {
  tablero: Tablero;
  columnas: TableroColumna[];
  tareas: Tarea[];
}

export interface TareasPaginadas {
  items: Tarea[];
  total: number;
  page: number;
  limit: number;
}

export interface ResumenMias {
  abiertas: number;
  vencidas: number;
  hoy: number;
  proximos: number;
  sinFecha: number;
}

export interface UsuarioAsignable {
  id: string;
  nombre: string;
  apellidoPaterno: string | null;
  rolId: string;
  fotoUrl: string | null;
  consultorioId: string | null;
  puedeVerTodasTareas: boolean;
}

export interface PlantillaColumna {
  nombre: string;
  color?: string;
  esFinal?: boolean;
}

export interface FiltrosTareas {
  tableroId?: string;
  columnaId?: string;
  asignadoId?: string;
  mias?: boolean;
  prioridad?: PrioridadTarea;
  etiquetaId?: string;
  consultorioId?: string;
  entidadTipo?: EntidadTipoTarea;
  entidadId?: string;
  q?: string;
  completadas?: 'si' | 'no';
  soloRaiz?: boolean;
  vencidas?: boolean;
  sinFecha?: boolean;
  vencimientoDesde?: string;
  vencimientoHasta?: string;
  page?: number;
  limit?: number;
}

export interface CrearTareaInput {
  titulo: string;
  tableroId: string;
  descripcion?: string;
  columnaId?: string;
  parentId?: string;
  prioridad?: PrioridadTarea;
  fechaVencimiento?: string;
  consultorioId?: string;
  asignadoIds?: string[];
  etiquetaIds?: string[];
  relaciones?: { entidadTipo: EntidadTipoTarea; entidadId: string }[];
}

export interface ActualizarTareaInput {
  titulo?: string;
  descripcion?: string | null;
  prioridad?: PrioridadTarea;
  fechaVencimiento?: string | null;
  consultorioId?: string | null;
  asignadoIds?: string[];
  etiquetaIds?: string[];
}

export interface ChecklistItem {
  id: string;
  texto: string;
  completado: boolean;
  completadoAt: string | null;
  orden: number;
}

export interface Checklist {
  id: string;
  titulo: string;
  orden: number;
  items: ChecklistItem[];
}

export interface ComentarioTarea {
  id: string;
  usuarioId: string;
  autorNombre: string;
  autorFotoUrl: string | null;
  contenido: string;
  menciones: { id: string; nombre: string }[];
  editadoAt: string | null;
  createdAt: string;
  esMio: boolean;
}

export interface AdjuntoTarea {
  id: string;
  nombreArchivo: string;
  mimeType: string;
  bytes: number;
  subidoPorId: string;
  subidoPorNombre: string;
  createdAt: string;
}

export interface ActividadTarea {
  id: string;
  usuarioId: string | null;
  usuarioNombre: string | null;
  accion: string;
  detalle: Record<string, any> | null;
  createdAt: string;
}

export const ADJUNTO_MAX_BYTES = 10 * 1024 * 1024;

export type TipoEntidadBuscable = 'consulta' | 'cita' | 'presupuesto';

/** Fila ligera para el selector de relaciones (no es el registro completo). */
export interface EntidadItem {
  id: string;
  tipo: TipoEntidadBuscable;
  fecha: string | null;
  titulo: string | null;
  medico: string | null;
  estado: string | null;
  /** Servicio de la cita o total del presupuesto. */
  detalle: string | null;
  motivoSugerencia?: string;
}

export interface EntidadesResumen {
  /** `presupuesto` es null cuando el rol no puede ver presupuestos. */
  conteos: { consulta: number; cita: number; presupuesto: number | null };
  sugeridos: EntidadItem[];
}

export interface EntidadesPagina {
  items: EntidadItem[];
  total: number;
  page: number;
  limit: number;
}

export const PRIORIDADES: { value: PrioridadTarea; label: string }[] = [
  { value: 'baja', label: 'Baja' },
  { value: 'media', label: 'Media' },
  { value: 'alta', label: 'Alta' },
  { value: 'urgente', label: 'Urgente' },
];
